import { BACKEND_DATE_FORMAT, BACKEND_DATE_TIME_FORMAT, HUMAN_STATUS, PRIORITY, RESOURCE_STATUS } from 'constants/consts';
import { EVENT_PHASES, EVENT_REPRESENTATION, EVENT_STATUS, EVENT_TYPE_DISPLAY_NAME, EVENT_TYPES, FlyProUI, PROPAGATE_SOURCE, SCHEDULED_EVENT_TYPES } from 'constants/event';
import { assign, clone, concat, difference, eq, filter, find, findIndex, flatMap, flatten, forEach, forOwn, get, head, isEmpty, isNull, lowerCase, map, orderBy, reduce, remove, set, size, some, sortBy, uniqBy, unset, } from 'lodash';
import moment from 'moment-timezone';
import { ICONOLOGY_ICONS, toastMessages } from 'constants/strings';
import * as toast from 'services/toasts';
import store from 'store';

/**
 * Get the position of a certain time
 */

moment.fn.roundNext15Min = function () {
  this.seconds(0);
  this.milliseconds(0);
  let intervals = Math.floor(this.minutes() / 15);
  if (this.minutes() % 15 !== 0) {
    intervals++;
  }
  if (intervals === 4) {
    this.add(1, 'hours');
    intervals = 0;
  }
  this.minutes(intervals * 15);
  return this;
};

export function getPrintLaneHeight(assetLength) {
  switch (true) {
    case (assetLength >= 30):
      return assetLength * 19.1;
    case (assetLength >= 25):
      return assetLength * 20.8;
    case (assetLength >= 18):
      return assetLength * 22;
    case (assetLength >= 14):
      return assetLength * 22.7;
    case (assetLength >= 10):
      return assetLength * 23.2;
    case (assetLength >= 8):
      return assetLength * 26.8;
    case (assetLength >= 6):
      return assetLength * 31.5;
    case (assetLength >= 4):
      return assetLength * 35.5;
    default:
      return FlyProUI.PRINT_LANE_HEIGHT;
  }
}

/**
 * Checks if an array of students contains invalid entries based on studentId uniqueness.
 *
 * @param {Array<{ value: { studentId: string } }>} students - An array of student objects.
 * @returns {boolean} - Returns true if there are duplicate studentIds, false otherwise.
 */
export function isStudentsInvalid(students) {
  const uniqueValues = new Set(students?.map(stu => stu?.value?.studentId));
  return uniqueValues?.size < students?.length;
}

/**
 * Orders event configuration objects by their base configuration type and categorizes them.
 *
 * @param {Object} event - The event object containing scheduled phase resources and configurations.
 * @param {Array} configs - An array of configuration objects.
 * @param {Array} configTypes - An array of configuration type objects.
 * @returns {Object} An object containing categorized configuration names.
 */
export function getEventConfigsByBaseConfig(event, configs, configTypes) {
  const eventConfigIds = map(flatMap(event.scheduled_phase_resources, 'configs'), 'config_id');
  let eventConfigs = map(eventConfigIds, cid => ({ ...find(configs, { id: cid }) }));
  eventConfigs = map(eventConfigs, c => ({ ...c, is_base_config_type: find(configTypes, { id: c.config_type })?.is_base_config_type }));
  const baseConfigs = map(filter(eventConfigs, { is_base_config_type: true }), 'name');
  const nonBaseConfigs = map(filter(eventConfigs, { is_base_config_type: false }), 'name');
  return { baseConfigs, nonBaseConfigs };
}

/**
 * Get event card tile color
 * @param {object} event - Event
 * @return {string} - Color
 */
export function getEventCardTileColor(event) {
  if (event.priority === PRIORITY.HIGH) {
    return '#ffffcc80';
  } else if (!isEmpty(event.external_resource)) {
    return '#ccffcc80';
  } else if (event.fly_pro_event_type === EVENT_TYPES.FCF_GR) {
    return '#f2f2f280';
  } else if (find(flatten(map(event.phases, 'scheduled_students')), s => s.priority === 1)) {
    return '#f2ccff80';
  } else if (!isEmpty(flatten(map(event.phases, 'scheduled_students')))) {
    return '#ccf2ff80';
  } else {
    return '#ffe6cc80';
  }
}

/**
 * Get the event phase of a lesson
 * @param {Object} lesson - The lesson
 */
export function getEventPhase(lesson) {
  return lesson.scheduled_phases[0];
}

/**
 * Get the Event phase of a lesson
 * @param {Object} lesson - The lesson
 */
export function getEventPhaseOfScheduledEvent(lesson) {
  return find(lesson.scheduled_phases, phase => phase.phase === EVENT_PHASES.EVENT);
}

/**
 * Get the fly event with flight updated
 * @param {object} lesson - Original event
 * @param {Array} resources - List of resources
 * @param {number} resourceShift - The shift in swimlane
 * @return {*} - The change of flight resource
 */
export function getEventResourceUpdated(lesson, resources, resourceShift) {
  const eventPhase = find(lesson.scheduled_phases, { phase: EVENT_PHASES.EVENT });
  const initialResourceIndex = findIndex(resources, resource => find(eventPhase.scheduled_phase_resources, r => r.resource_id === resource.resourceId));
  eventPhase.scheduled_phase_resources = map(eventPhase.scheduled_phase_resources, re => re.resource_id === resources[initialResourceIndex].resourceId ? { ...re, icon: ICONOLOGY_ICONS.SQUARE.value, qualifications: map(re.qualifications, q => ({ qualification: q.qualification, resource_id: resources[initialResourceIndex + resourceShift].resourceId })), resource_id: resources[initialResourceIndex + resourceShift].resourceId } : re);
  return eventPhase;
}

/**
 * Get event type name to display
 * @param {string} type - Event type
 * @return {string} - Event type name to display
 */
export function getEventTypeName(type) {
  switch (type) {
    case EVENT_TYPES.FLIGHT:
      return 'Flight';
    case EVENT_TYPES.FCF_GR:
      return 'FCF/GR';
    case EVENT_TYPES.SIM:
      return 'Simulation';
    case EVENT_TYPES.COHORT:
      return 'Ground School';
    default:
      return '';
  }
}

/**
 * Get the display name for a event type
 * @param {string} lessonType - Lesson type
 * @return {*}
 */
export function getLessonTypeDisplayName(lessonType) {
  return EVENT_TYPE_DISPLAY_NAME[find(EVENT_TYPES, type => type === lessonType)];
}

/**
 * Retrieves participants from a given data set based on the current phase of an event and whether the participants are human.
 *
 * @param {Object} data - The data containing scheduled phases and participants information.
 * @param {Object} cardPhase - The current phase of the event card (e.g., EVENT, etc.).
 * @param {boolean} humanRow - A flag to filter only human participants based on certain conditions.
 *
 * @returns {Array} An array of participants based on the phase and filter conditions.
 */
export function getParticipants(data, cardPhase, humanRow) {
  const filterParticipants = (phase) => {
    return humanRow
      ? filter(phase.scheduled_phase_humans, ph => (ph.lesson_phase_human_id || ph.enrolment_id))
      : phase.scheduled_phase_humans;
  };
  let participants = [];
  if (cardPhase.phase === EVENT_PHASES.EVENT) {
    participants = data.scheduled_phases.reduce((eventPhaseHumans, p) => {
      return concat(eventPhaseHumans, filterParticipants(p));
    }, []);
  } else {
    participants = filterParticipants(cardPhase);
  }
  return participants;
}

/**
 * Orders an array of participants based on specific criteria.
 *
 * This function takes an array of participant objects and returns an ordered array
 * based on the participants' qualifications' card display order, last name, and first name.
 *
 * @param {Array} participants - An array of participant objects.
 * @returns {Array} An ordered array of participant objects.
 */
export function getOrderedParticipants(participants) {
  const orderedParticipants = !isEmpty(participants) &&
    orderBy(participants, [
      o => map(o?.qualifications, 'card_display_order'),
      o => o?.human_last_name,
      o => o?.human_first_name,
    ], ['asc', 'asc', 'asc']);
  return orderedParticipants;
}

/**
 * Enhances event participants with their qualifications from the allHumans array.
 *
 * @param {Array} eventParticipants - An array of event participant objects.
 * @param {Array} allHumans - An array of all human objects with qualifications.
 * @returns {Array} - An array of enhanced event participant objects containing qualifications.
 */
export function enhanceParticipantsWithQuals(eventParticipants, allHumans) {
  return map(eventParticipants, p => (
    { ...p, qualifications: find(allHumans, h => h.id === p.human_id)?.qualifications || [] }
  ));
}

/**
 * Return a resource descriptors
 * @param {string} resourceId
 * @returns {{name: *, url: null}}
 */
export function getResourceDescriptors(dayResourcesMetadata, resourceId) {
  const descriptors = find(dayResourcesMetadata, { resource_id: resourceId });
  return descriptors;
}

/**
 * Retrieves human descriptors (top and bottom metadata fields) for a given human resource.
 * @param {Array<Object>} dayHumanMetadata - Array of human metadata objects for the current day.
 * @param {string} humanId - The unique identifier of the human resource.
 * @returns {Object|undefined} Human descriptor object containing top_descriptor and bottom_descriptor properties, or undefined if not found.
 */
export function getHumanDescriptors(dayHumanMetadata, humanId) {
  const descriptors = find(dayHumanMetadata, { human_id: humanId });
  return descriptors;
}

/**
 * Return a resource link
 * @param {string} datasetId
 * @param {Object} resource
 * @returns {{name: *, url: null}}
 */
export function getResourceLink(datasetId, resource) {
  const link = { name: resource.resourceName, url: null };
  link.url = `/datasets/${datasetId}/resource-types/${resource.resourceType}/resources/${resource.resourceId}`;
  return link;
}

/**
 * Extract student properties and related information.
 *
 * @param {Object} student - The student object.
 * @param {Object[]} cohorts - An array of cohort objects.
 * @param {Object[]} allSyllabi - An array of syllabus objects.
 * @returns {Object} An object containing extracted properties.
 */
export function getStudentProp(student, cohorts, allSyllabi) {
  const cohort = find(cohorts, { id: student?.cohort_id });
  return {
    studentId: student.id,
    enrolmentId: student.enrolment_id,
    enrolledLessonId: student.enrolled_lesson_id,
    cohortName: cohort?.name,
    cohortIsArchived: cohort?.is_archived,
    cohortId: student?.cohort_id,
    syllabusName: find(allSyllabi, { id: student?.syllabus_id })?.syllabus_name,
  };
}

/**
 * Transform student objects into options for a selection control.
 *
 * @param {Object[]} students - An array of student objects.
 * @param {Object[]} cohorts - An array of cohort objects.
 * @param {Object[]} allSyllabi - An array of syllabus objects.
 * @returns {Object[]} An array of student options with label and value properties.
 */
export const getStudentOptions = (students, cohorts, allSyllabi) => {
  return map(students, stu => {
    return { label: stu.name, value: { ...getStudentProp(stu, cohorts, allSyllabi) } };
  });
};

/**
 * Retrieves a student option matching the selected enrollment from all event representation students.
 *
 * @param {Object} selectedEnrolment - The selected enrollment object with value containing enrolmentId.
 * @param {Array<Object>} allEventRepStudents - An array of all event representation student objects.
 * @param {Array<Object>} cohorts - An array of cohort objects.
 * @param {Array<Object>} allSyllabi - An array of syllabus objects.
 * @returns {Object|null} A student option object with label and value properties, or null if not found or no enrollment selected.
 */
export function getEnrolmentStudentOption(selectedEnrolment, allEventRepStudents, cohorts, allSyllabi) {
  if (!selectedEnrolment) return null;
  const matchedStudent = find(allEventRepStudents, s => s.enrolment_id === selectedEnrolment.value.enrolmentId);
  return matchedStudent ? getStudentOptions([matchedStudent], cohorts, allSyllabi)[0] || null : null;
}

/**
 * Generates an array of student options based on the given cohort's humans,
 * using data from allStudents, cohort, and allSyllabi.
 *
 * @param {Array} allStudents - An array of all student objects.
 * @param {Object} cohort - The selected cohort object.
 * @param {Array} cohorts - An array of all existing cohort objects.
 * @param {Array} allSyllabi - An array of all syllabi objects.
 * @returns {Array} An array of student options with labels and values.
 */
export const getStudentsOptionsInSelectedCohort = (cohort, allStudents, cohorts, allSyllabi) => {
  const cohortStudents = filter(allStudents, { cohort_id: cohort.id });
  return map(cohortStudents, stu => ({ label: stu.name, value: { ...getStudentProp(stu, cohorts, allSyllabi) } }));
};

/**
 * Generates an array of options for student and cohort selections.
 *
 * @param {Object[]} humansEnrolledInSourceLesson - An array of student objects enrolled in the source lesson.
 * @param {Object[]} humansEnrolledInTargetLesson - An array of student objects enrolled in the target lesson.
 * @param {Object[]} cohorts - An array of cohort objects containing cohort information.
 * @param {Object[]} allSyllabi - An array of all the syllabi exists.
 * @returns {Object[]} - An array of options for student and cohort selections.
 */
export const getStudentAndCohortOptions = (humansEnrolledInSourceLesson, humansEnrolledInTargetLesson, cohorts, allSyllabi) => {
  const EVENT_REP_LABELS = EVENT_REPRESENTATION.labels;
  const getStudentsGroup = (label, students) => [{
    label,
    options: orderBy(getStudentOptions(students, cohorts, allSyllabi), ['label'], ['asc']),
  }];
  const getCohortOptions = (label, items) => [{
    label,
    options: map(orderBy(filter(items, c => !c.is_archived), ['name'], ['asc']), c => ({ label: c.name, value: { ...c, isCohort: true } }))
  }];
  const sourceLessonOptions = getStudentsGroup(EVENT_REP_LABELS.HUMANS_CURRENTLY_ENROLLED_IN_SOURCE_LESSON, humansEnrolledInSourceLesson);
  const targetLessonOptions = humansEnrolledInTargetLesson ? getStudentsGroup(EVENT_REP_LABELS.HUMANS_CURRENTLY_ENROLLED_IN_TARGET_LESSON, humansEnrolledInTargetLesson) : [];
  const cohortOptions = getCohortOptions(EVENT_REP_LABELS.COHORTS, cohorts);
  return concat(sourceLessonOptions, targetLessonOptions, cohortOptions);
};

/**
 * Combines and returns an array of all students enrolled in both source and target lessons.
 *
 * @param {Object[]} humansEnrolledInSourceLesson - An array of student objects enrolled in the source lesson.
 * @param {Object[]} humansEnrolledInTargetLesson - An array of student objects enrolled in the target lesson.
 * @returns {Object[]} - An array containing all students enrolled in both source and target lessons.
 */
export const getAllEventRepStudents = (humansEnrolledInSourceLesson, humansEnrolledInTargetLesson) => {
  return [...humansEnrolledInSourceLesson || [], ...humansEnrolledInTargetLesson || []];
};

/**
 * Return a human link
 * @param {string} datasetId
 * @param {Object} human
 * @returns {{name: *, url: null}}
 */
export function getHumanLink(datasetId, human, LHS_Humans) {
  const link = { name: human.resourceName, url: null };
  const LHS_Item = find(LHS_Humans, (lhs_item) => {
    return find(lhs_item.humans, { id: human.resourceId });
  });
  link.url = `/datasets/${datasetId}/personnel/${lowerCase(LHS_Item?.name)}/${human.resourceId}`;
  return link;
}

export const getMaskedPhaseName = (phaseName) => {
  let maskedName = phaseName;
  if (phaseName === EVENT_PHASES.PREP) maskedName = EVENT_PHASES.BRIEF;
  if (phaseName === EVENT_PHASES.BRIEF) maskedName = EVENT_PHASES.PREP;
  return maskedName;
};

/**
 * Calculate the total actual and planned times for a list of lessons.
 *
 * @param {Array<Object>} lessons - An array of lesson objects.
 * @returns {{ actual: number, planned: number }} - An object containing the total actual and planned times in hours.
 */
export function getResourceTimes(lessons) {
  const times = {
    actual: null,
    planned: 0
  };
  lessons.forEach((l) => {
    const phase = l.phase;
    if (phase && l.event_status !== EVENT_STATUS.CANCELLED) {
      times.planned += moment.duration(moment(phase.end_time).diff(moment(phase.start_time))).asHours();
      const actualDuration = phase.actual_duration && moment.duration(phase.actual_duration, 'minutes').asHours().toFixed(1);
      if (!isNull(actualDuration) && l.scheduled_event_type === SCHEDULED_EVENT_TYPES.FLIGHT_EVENT && l.event_status !== EVENT_STATUS.PLANNED && l.event_status !== EVENT_STATUS.CANCELLED) {
        times.actual += Number(actualDuration);
      }
    }
  });
  return times;
}

/**
 * Get a list of unique ressources used
 * @param {Array} events - List of events
 * @return {Array} - List of resources
 */
export function getResources(events) {
  return sortBy(uniqBy(reduce(events, (result, event) => {
    const resource = find(event.scheduled_phases[0].scheduled_phase_resources, resource => true);
    if (resource) {
      return concat(result, { resourceId: resource.resource_id, resourceName: resource.resource_name, resourceType: resource.resource_type_id, isStatic: resource.is_static });
    } else {
      return result;
    }
  }, []), 'resourceId'), 'resourceName');
}

/**
 * Get a list of unique humans used
 * @param {Array} events - List of events
 * @return {Array} - List of humanss
 */
export function getHumans(events) {
  const humans = [];
  map(events, event => {
    map(event?.scheduled_phases[0]?.scheduled_phase_humans, human => {
      humans.push({ resourceId: human.human_id, resourceName: human.human_name });
    });
  });
  return humans;
}

/**
 * Get a list of human options for requirement selection in the event modal.
 * @param {Array} resources - List of resources
 * @param {Object} lesson - The lesson object
 * @param {Object} scheduledEvent - The scheduled event object
 * @param {Object} requirement - The requirement object
 * @param {number} school - The school ID
 * @param {Array} qualFilters - List of qualification filters
 * @return {Array} - List of formatted human options
 */
export function getHumanOptions(resources, lesson, scheduledEvent, requirement, school, qualFilters) {
  const EVENT_REP_PROPS = EVENT_REPRESENTATION.properties;
  const firstGroup = [];
  const secondGroup = [];
  const lastGroup = [];
  let filteredHumans = filter(resources, { status: HUMAN_STATUS.ACTIVE });
  filteredHumans = filter(filteredHumans, h => !some(h?.ineligibleResources, { id: lesson.id }));
  filteredHumans = eq(scheduledEvent[EVENT_REP_PROPS.SCHEDULED_EVENT_TYPE], SCHEDULED_EVENT_TYPES.FLIGHT_EVENT) ? filter(filteredHumans, { can_use_for_flying_event: true }) : filteredHumans;
  filteredHumans = eq(scheduledEvent[EVENT_REP_PROPS.SCHEDULED_EVENT_TYPE], SCHEDULED_EVENT_TYPES.SIMULATOR_EVENT) ? filter(filteredHumans, { can_use_for_sim_event: true }) : filteredHumans;
  filteredHumans = !isEmpty(qualFilters)
    ? filter(filteredHumans, h => isEmpty(difference(map(qualFilters, 'value'), map(h.qualifications, 'id'))))
    : filteredHumans;
  map(filteredHumans, h => {
    const humanQualIds = map(h.qualifications, 'id');
    const selectedReqQualIds = map(requirement?.value.qualifications, 'qualification_id');
    if (isEmpty(difference(selectedReqQualIds, humanQualIds))) { // all first entries are into second arrau
      if (isEmpty(h.school_availability) || !find(h.school_availability, p => p.school_id === school && p.availability_percent > 0)) {
        secondGroup.push(h);
      } else {
        firstGroup.push(h);
      }
    } else {
      lastGroup.push(h);
    }
  });
  return [
    ...orderBy(firstGroup, ['name']),
    { id: null, name: null },
    ...orderBy(secondGroup, ['name']),
    ...orderBy(lastGroup, ['name'])
  ];
}

/**
 * Get the resource config options for the event modal.
 * @param {Array} resources - List of resources
 * @param {Array} configs - The resourceType configs
 * @param {Array} groups - The resourceType config groups
 * @param {Array} groupsWithConfigs - The resourceType config groups with their configs listed for each group
 * @param {Object} resource - The event modal resource object
 * @return {{filteredConfigs: Array, filteredConfigGroups: Array}} - filteredConfigs list and filteredConfigGroups list
 */
export function getResourceConfigOptions(resources, configs, groups, groupsWithConfigs, resource) {
  let filteredConfigs = configs;
  let filteredConfigGroups = groups;
  if (resource) {
    const fullResource = find(resources, r => r.id === resource?.value);
    filteredConfigs = filter(configs, c => some(fullResource.configurations, rc => rc.id === c.id));
    filteredConfigGroups = filter(groupsWithConfigs, cg => {
      return difference(map(cg.configs, 'id'), map(fullResource.configurations, 'id')).length === 0;
    });
  }
  return { filteredConfigs, filteredConfigGroups };
}

/**
 * Get a list of resource options for requirement selection in the event modal.
 * @param {Array} resources - List of resources
 * @param {Object} requirement - The requirement object
 * @param {number} school - The school ID
 * @param {Array} configFilters - List of configuration filters
 * @return {Array} - List of formatted resource options
 */
export function getResourceOptions(resources, requirement, school, configFilters) {
  const firstGroup = [];
  const secondGroup = [];
  const lastGroup = [];
  let filteredResources = filter(resources, { status: RESOURCE_STATUS.ACTIVE }); //Current Resources
  filteredResources = filter(filteredResources, resource => resource.resource_type_id === requirement?.value.resource_type_id); // Resources Of Resource Type
  filteredResources = !isEmpty(configFilters)
    ? filter(filteredResources, h => isEmpty(difference(map(configFilters, 'value'), map(h.configurations, 'id'))))
    : filteredResources;
  map(filteredResources, r => {
    const resourceConfigIds = map(r.configurations, 'id');
    const selectedReqConfigIds = map(requirement?.value.configs, 'config_id');
    if (isEmpty(difference(selectedReqConfigIds, resourceConfigIds))) {
      if (isEmpty(r.school_availability) || !find(r.school_availability, p => p.school_id === school && p.availability_percent > 0)) {
        secondGroup.push(r);
      } else {
        firstGroup.push(r);
      }
    } else {
      lastGroup.push(r);
    }
  });
  return [
    ...orderBy(firstGroup, ['display_order', 'name']),
    { id: null, name: null },
    ...orderBy(secondGroup, ['display_order', 'name']),
    ...orderBy(lastGroup, ['display_order', 'name'])
  ];
}

/**
 * Get warning when updating past event
 * @param {object} lesson
 * @param {string} timeZone
 */
export function getPastEventWarning(lesson, timeZone) {
  const eventPhase = getEventPhase(lesson);
  const currentTime = moment().tz(timeZone);
  if (moment.tz(eventPhase.end_time, timeZone).isBefore(currentTime)) {
    toast.warning(toastMessages.UPDATE_PAST_EVENT_WARNING, 3000);
  }
}

export function getStaffByEvent(event) {
  return filter(event.scheduled_phase_humans, (resource) => {
    return resource.human_resource === true;
  });
}

/**TODO... Should be  removed after clients confrims the new staff name doisplay on cards */
export function getStaffInitials(staff) {
  const words = staff.split(' ');
  if (size(words) > 1) {
    return map(staff.split(' '), (item) => {
      return `${head(item)}`;
    });
  } else {
    return staff;
  }
}

/**
 * Get time updated lesson
 * @param {object} lesson - The old lesson object
 * @param {object} newStartTime - The new start time moment object
 * @param {object} newEndTime - The new end time moment object
 * @param {object} phase - Event phase object
 * @return {*}
 */
export function getTimeUpdatedLesson(lesson, newStartTime, newEndTime, phase) {
  const dateFormat = 'YYYY-MM-DDTHH:mm:ss';
  const startTimeDiffMinutes = newStartTime.diff(moment(phase.start_time, dateFormat), 'minutes');
  const endTimeDiffMinutes = newEndTime.diff(moment(phase.end_time, dateFormat), 'minutes');
  const newLesson = clone(lesson);
  forEach(newLesson.scheduled_phases, (p) => {
    if (phase.phase !== p.phase) {
      if (moment(p.start_time).isBefore(moment(phase.start_time))) {
        const start = moment(p.start_time).add(startTimeDiffMinutes, 'minutes');
        const end = moment(p.end_time).add(startTimeDiffMinutes, 'minutes');
        p.start_time = start.isValid() ? start.format(dateFormat) : null;
        p.end_time = end.isValid() ? end.format(dateFormat) : null;
      } else {
        const start = moment(p.start_time).add(endTimeDiffMinutes, 'minutes');
        const end = moment(p.end_time).add(endTimeDiffMinutes, 'minutes');
        p.start_time = start.isValid() ? start.format(dateFormat) : null;
        p.end_time = end.isValid() ? end.format(dateFormat) : null;
      }
    } else {
      p.start_time = newStartTime.isValid() ? newStartTime.format(dateFormat) : null;
      p.end_time = newEndTime.isValid() ? newEndTime.format(dateFormat) : null;
    }
  });
  return newLesson;
}

/**
 * Get position of the sortie from time
 * @param {moment} originTime - The start time of the time line
 * @param {number} width - The swimlane width
 * @param {moment} time - The time for the position
 * @returns {number}
 */
export function getPositionFromTime(originTime, width, time) {
  const diffMinutes = time?.diff(originTime, 'seconds');
  return diffMinutes / 24 / 60 / 60 * width;
}

/**
 * Get time from the position of the sortie
 * @param {moment} originTime - The start time of the time line
 * @param {number} width - The swimlane width
 * @param {number} position - The position of the sortie
 * @returns {*}
 */
export function getTimeFromPosition(originTime, width, position) {
  const minPerPixel = 24 * 60 / width;
  const timeInMins = position * minPerPixel;
  return originTime.clone().add(timeInMins, 'minute').roundNext15Min();
}

/**
 * Get the start time of the day for a given date and time zone.
 *
 * @param {Date} date - The date for which to get the start time of the day.
 * @param {string} timeZone - The time zone to use for the calculation.
 * @returns {moment} - The moment object representing the start time of the day.
 */
export function getStartTimeOfDay(date, timeZone) {
  return moment.tz(date, timeZone).hour(6).minute(0).second(0);
}

/**
 * Get the start time of the next day for a given date and time zone.
 *
 * @param {Date} date - The date for which to get the start time of the next day.
 * @param {string} timeZone - The time zone to use for the calculation.
 * @returns {moment} - The moment object representing the start time of the next day.
 */
export function getStartTimeOfNextDay(date, timeZone) {
  return moment.tz(date, timeZone).hour(6).minute(0).second(0).add(1, 'days');
}

/**
 * Get the start time of the day for a given date and time zone, formatted in the backend date-time format.
 *
 * @param {Date} date - The date for which to get the start time of the day.
 * @param {string} timeZone - The time zone to use for the calculation.
 * @returns {string} - The formatted start time of the day in the backend date-time format.
 */
export function getStartTimeOfDayBackendFormat(date, timeZone) {
  return getStartTimeOfDay(date, timeZone).format(BACKEND_DATE_TIME_FORMAT);
}

/**
 * Get the start time of the next day for a given date and time zone, formatted in the backend date-time format.
 *
 * @param {Date} date - The date for which to get the start time of the next day.
 * @param {string} timeZone - The time zone to use for the calculation.
 * @returns {string} - The formatted start time of the next day in the backend date-time format.
 */
export function getStartTimeOfNextDayBackendFormat(date, timeZone) {
  return getStartTimeOfNextDay(date, timeZone).format(BACKEND_DATE_TIME_FORMAT);
}

/**
 * Retrieve and remove the stored FlyPro date from local store for a given user.
 * @param {string} userId
 * @returns {string|undefined} - The stored FlyPro date if present; otherwise undefined.
 */

export function consumeLocalFlyproDate(userId) {
  const key = `flyproDate/${userId}`;
  const storedDate = store.get(key);
  
  if (!storedDate) return undefined;
  
  store.remove(key);
  return storedDate;
}

/**
 * Set a FlyPro date for a given user.
 * @param {string} date - The date-time string to store, formatted in the backend date-time format.
 * @param {string} userId
 * @param {string} datasetId
 * @returns {void}
 */
export function setFlyproDate(date, userId, datasetId) {
  store.set(`flyproDate/${userId}`, moment(date, BACKEND_DATE_TIME_FORMAT).format(BACKEND_DATE_FORMAT));
}

/**
 * Get the resize width of the sortie
 * @param {string} resizeStopWidth - The resized width of the sortie
 * @returns {number} - The width
 */
export function getSortieResizeWidth(resizeStopWidth) {
  const initialWidth = resizeStopWidth.substring(0, size(resizeStopWidth) - 2);
  return Number(initialWidth);
}

/**
 * Get the sortie width
 * @param {number} canvasWidth - Width of the canvas
 * @param {string} date - Current date of flypro
 * @param {string} startTime - Start time of sortie event
 * @param {string} endTime - End time of sortie event
 * @param {string} timeZone - time zone
 * @return {number} - The sortie's width
 */
export function getSortieWidth(canvasWidth, date, startTime, endTime, timeZone) {
  const startLineTime = getStartTimeOfDay(date, timeZone);
  const start = getPositionFromTime(startLineTime, canvasWidth, moment.tz(startTime, timeZone));
  const end = getPositionFromTime(startLineTime, canvasWidth, moment.tz(endTime, timeZone));
  return end - start;
}

/**
 * Get student partial last event
 * @param {string} humanId - Human ID
 * @param {[]} humansWithPartialLastLessons - students with partial last lessons
 * @param {string} eventType - Event type
 * @return {{}} - Partial event record
 */
export function getStudentPartialLastEvents(humanId, humansWithPartialLastLessons, eventType) {
  return find(get(find(humansWithPartialLastLessons, h => h.human === humanId), 'partially_completed_last_lessons', []),
    l => l.event_type.toLowerCase() === eventType.toLowerCase());
}

/**
 * Sets the "required" property for a specific phase requirement.
 *
 * @param {Phase} phase - The phase for which the requirement is being modified.
 * @param {Object} params - Parameters controlling the behavior of the function.Ω
 * @returns {Phase} - The modified phase object with updated requirement properties.
 */
export function setPhaseRequired(phase, params) {
  const { requirement } = params;
  const phaseRequirement = find(phase.requirements, pr => pr.groupId === requirement?.groupId);
  phaseRequirement && unset(phaseRequirement, 'notRequired');
  return phase;
}

/**
 * Checks if fuel is displayed based on the provided phases details, resource types, and resources.
 *
 * @param {Array} phasesDetails - The details of phases to be checked.
 * @param {Array} resourceTypes - The types of resources available.
 * @param {Array} resources - The available resources.
 * @returns {boolean} Returns true if fuel is displayed; otherwise, returns false.
 */
export function isFuelDisplayedOnEventModal(phasesDetails, resourceTypes, resources) {
  const isFuelDisplayed = some(phasesDetails, (phase) => {
    const requirementResourcesUsingFuel = some(phase.requirements, r => {
      return find(resourceTypes, { id: find(resources, { id: r?.selectedResource?.value })?.resource_type_id })?.is_using_fuel;
    });
    const additionalResourcesUsingFuel = some(phase.additionalResources, r => {
      return find(resourceTypes, { id: find(resources, { id: r?.value })?.resource_type_id })?.is_using_fuel;
    });
    return requirementResourcesUsingFuel || additionalResourcesUsingFuel;
  });
  return isFuelDisplayed;
}

/**
 * Checks if fuel is displayed on a card based on the provided event, resource types, and resources.
 *
 * @param {object} event - The event object.
 * @param {Array} resourceTypes - An array of resource types.
 * @returns {boolean} Returns true if fuel is displayed on the card, otherwise false.
 */
export function isFuelDisplayedOnCard(event, resourceTypes) {
  return some(event.scheduled_phase_resources, sr => {
    return find(resourceTypes, { id: sr?.resource_type_id })?.is_using_fuel;
  });
}

/**
 * Sets and propagates the "required" property for phases in the provided phasesDetails object.
 *
 * @param {Object.<string, Phase>} phasesDetails - An object containing phase details keyed by phase name.
 * @param {Object} params - Parameters controlling the behavior of the function.
 * @returns {Object.<string, Phase>} - The modified phasesDetails object with updated "required" property.
 */
export function setAndPropagateRequired(phasesDetails, params) {
  const { autofills, propagateSource, isEdit } = params;
  forOwn(phasesDetails, (phase, key) => {
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && setPhaseRequired(phase, params)
      : key === EVENT_PHASES.EVENT
        ? setPhaseRequired(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && setPhaseRequired(phase, params);
  });
  return phasesDetails;
}

/**
 * Sets the "not required" property for a specific phase requirement and unsets the selected resource.
 *
 * @param {Phase} phase - The phase for which the requirement is being modified.
 * @param {Object} params - Parameters controlling the behavior of the function.
 * @returns {Phase} - The modified phase object with updated requirement properties.
 */
export function setPhaseNotRequired(phase, params) {
  const { requirement } = params;
  const phaseRequirement = find(phase.requirements, pr => pr.groupId === requirement?.groupId);
  if (phaseRequirement) {
    const selectedTags = phaseRequirement.selected.value.tags ? map(phaseRequirement.selected.value.tags, t => ({ value: t.tag_id, label: t.tag_name })) : undefined;
    const selectedConfigs = phaseRequirement.selected.value.configs ? map(phaseRequirement.selected.value.configs, c => ({ value: c.config_id, label: c.config_name })) : undefined;
    
    set(phaseRequirement, 'notRequired', true);
    set(phaseRequirement, 'selectedTags', selectedTags);
    set(phaseRequirement, 'selectedConfigs', selectedConfigs);
    unset(phaseRequirement, 'selectedResource');
  }
  return phase;
}

/**
 * Sets and propagates the "not required" property for phases in the provided phasesDetails object.
 *
 * @param {Object.<string, Phase>} phasesDetails - An object containing phase details keyed by phase name.
 * @param {Object} params - Parameters controlling the behavior of the function.
 * @param {boolean} params.isEdit - Indicates if the operation is in edit mode.
 * @returns {Object.<string, Phase>} - The modified phasesDetails object with updated "not required" property.
 */
export function setAndPropagateNotRequired(phasesDetails, params) {
  const { autofills, propagateSource, isEdit } = params;
  forOwn(phasesDetails, (phase, key) => {
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && setPhaseNotRequired(phase, params)
      : key === EVENT_PHASES.EVENT
        ? setPhaseNotRequired(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && setPhaseNotRequired(phase, params);
  });
  return phasesDetails;
}

/**
 * Sets the 'selected' property for a specific phase requirement in the 'phasesDetails' object
 * and removes the 'selectedResource' property from the requirement.
 *
 * @param {Object} phase - An object containing phase details.
 * @param {Object} params - An object containing parameters for setting the phase requirement.
 * @param {number} params.index - The index value used to identify the specific phase requirement.
 * @param {*} params.selected - The value to set as the 'selected' property for the requirement.
 * @param {string} params.phaseName - The name of the phase in the 'phasesDetails' object.
 * @returns {Object} - The updated 'phasesDetails' object with the 'selected' property set and 'selectedResource' property removed.
 */
export function setPhaseRequirement(phase, params) {
  const { index, selected } = params;
  const phaseRequirement = phase.requirements[index];
  set(phaseRequirement, 'selected', selected);
  unset(phaseRequirement, 'selectedResource');
  set(phaseRequirement, 'selectedTags', selected.value.tags ? map(selected.value.tags, t => ({ value: t.tag_id, label: t.tag_name })) : undefined);
  set(phaseRequirement, 'selectedConfigs', selected.value.configs ? map(selected.value.configs, c => ({ value: c.config_id, label: c.config_name })) : undefined);
  return phase;
}

/**
 * * Sets the 'selected' property for a specific phase requirement in the 'phasesDetails' array
 *  * and removes the 'selectedResource' property from each requirement.
 *  *
 *  * @param {Array} phasesDetails - An array containing phase details.
 *  * @param {Object} params - An object containing parameters for setting the phase requirement.
 *  * @param {number} params.index - The index value used to find the appropriate phase requirement.
 *  * @param {Object} params.selected - An object representing the selected requirement.
 *  * @returns {Array} - The updated 'phasesDetails' array with the 'selected' property set.
 *  */
export function setAndPropagateRequirement(phasesDetails, params) {
  const { selected, propagateSource, autofills, isEdit } = params;
  const propagate = (phase, phaseRequirement) => {
    const phaseSelectedReq = find(phaseRequirement?.options, option => {
      return selected.autoRequirementId === option.autoRequirementId;
    });
    set(phaseRequirement, 'selected', phaseSelectedReq);
    unset(phaseRequirement, 'selectedResource');
    if (phaseSelectedReq) {
      set(phaseRequirement, 'selectedTags', phaseSelectedReq.value.tags ? map(phaseSelectedReq.value.tags, t => ({ value: t.tag_id, label: t.tag_name })) : undefined);
      set(phaseRequirement, 'selectedConfigs', phaseSelectedReq.value.configs ? map(phaseSelectedReq.value.configs, c => ({ value: c.config_id, label: c.config_name })) : undefined);
    }
  };
  forOwn(phasesDetails, (phase, key) => {
    const phaseRequirement = find(phase.requirements, { groupId: selected.groupId });
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && propagate(phase, phaseRequirement)
      : key === EVENT_PHASES.EVENT
        ? setPhaseRequirement(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && phaseRequirement && propagate(phase, phaseRequirement);
  });
  return phasesDetails;
}

/**
 * Updates the resource tags and configurations for a specific phase in the 'phasesDetails' object.
 *
 * @param {Object} phase - An object containing phase details.
 * @param {Object} params - An object containing parameters for updating the phase resource.
 * @param {string} params.phaseName - The name of the phase in the 'phasesDetails' object.
 * @param {*} params.<other_parameters> - Other parameters required for updating the resource tags and configurations.
 * @returns {Object} - The updated 'phasesDetails' object with the phase resource updated.
 */
export function setPhaseResource(phase, params) {
  setResourceTagsConfigs(phase, params);
  return phase;
}

/**
 * Updates the resource tags and configurations for all phases in the 'phasesDetails' object
 * using the provided parameters.
 *
 * @param {Object} phasesDetails - An object containing phase details indexed by phase names.
 * @param {Object} params - An object containing parameters for updating the phase resources.
 * @param {*} params.<other_parameters> - Other parameters required for updating the resource tags and configurations.
 * @returns {Object} - The updated 'phasesDetails' object with all phase resources updated.
 */
export function setAndPropagateResource(phasesDetails, params) {
  const { autofills, propagateSource, isEdit } = params;
  forOwn(phasesDetails, (phase, key) => {
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && setPhaseResource(phase, params)
      : key === EVENT_PHASES.EVENT
        ? setPhaseResource(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && setPhaseResource(phase, params);
  });
  return phasesDetails;
}

/**
 * Updates the resource tags and configurations for a specific phase requirement.
 * It also removes the selected resource and associated tags and configurations from other requirements within the phase, if the same is selected
 * Additionally, it removes the selected resource from the 'students', 'additionalCrew', and 'additionalResources' arrays of the phase.
 *
 * @param {Object} phase - The phase object containing the requirements and resource details.
 * @param {Object} params - An object containing parameters for updating the phase resource tags and configurations.
 * @param {number} params.index - The index of the phase requirement to update.
 * @param {*} params.selectedResource - The selected resource to set for the specified requirement.
 * @param {*} params.selectedTags - The selected tags to set for the specified requirement.
 * @param {*} params.selectedConfigs - The selected configurations to set for the specified requirement.
 * @returns {Object} - The updated phase object after setting the resource tags and configurations.
 */
function setResourceTagsConfigs(phase, params) {
  const { selectedResource, selectedTags, selectedConfigs, requirement } = params;
  map(phase.requirements, r => {
    if (r.groupId === requirement?.groupId) {
      set(r, 'selectedResource', selectedResource);
      set(r, 'selectedTags', selectedTags);
      set(r, 'selectedConfigs', selectedConfigs);
    } else {
      if (r?.selectedResource?.value === selectedResource?.value) {
        unset(r, 'selectedResource');
        set(r, 'selectedTags', r.selected.value.tags ? map(r.selected.value.tags, t => ({ value: t.tag_id, label: t.tag_name })) : undefined);
        set(r, 'selectedConfigs', r.selected.value.configs ? map(r.selected.value.configs, c => ({ value: c.config_id, label: c.config_name })) : undefined);
      }
    }
  });
  remove(phase.students, stu => stu?.value?.studentId === selectedResource?.value);
  remove(phase.additionalCrew, c => c.value === selectedResource?.value);
  remove(phase.additionalResources, c => c.value === selectedResource?.value);
  return phase;
}

/**
 * Sets the studentIds for a specific phase in the 'phasesDetails' object
 * and removes the selected resource from the phase requirements and additionalCrew.
 *
 * @param {Object} phase - An object containing phase details.
 * @param {Object} params - An object containing parameters for setting the phase students.
 * @param {string} params.phaseName - The name of the phase in the 'phasesDetails' object.
 * @param {Array} params.studentIds - An array of student IDs to set for the phase.
 * @returns {Object} - The updated 'phasesDetails' object with the studentIds and removed selected resources.
 */
export function setPhaseStudents(phase, params) {
  const { students } = params;
  set(phase, 'students', students);
  map(students, stu => {
    find(phase.requirements, req => req?.selectedResource?.value === stu?.value?.studentId && unset(req, 'selectedResource'));
    set(phase, 'additionalCrew', filter(phase?.additionalCrew, ac => ac.value !== stu?.value?.studentId));
  });
  return phase;
}

/**
 * Sets the studentIds for all phases in the 'phasesDetails' object
 * and removes the selected resource from the phase requirements and additionalCrew for each phase.
 *
 * @param {Object} phasesDetails - An object containing phase details indexed by phase names.
 * @param {Object} params - An object containing parameters for setting the phase students.
 * @returns {Object} - The updated 'phasesDetails' object with the studentIds and removed selected resources for all phases.
 */
export function setAndPropagateStudents(phasesDetails, params) {
  const { propagateSource, autofills, isEdit } = params;
  forOwn(phasesDetails, (phase, key) => {
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && setPhaseStudents(phase, params)
      : key === EVENT_PHASES.EVENT
        ? setPhaseStudents(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && setPhaseStudents(phase, params);
  });
  return phasesDetails;
}

/**
 * Sets the selected additional humans (crew) for a specific phase in the 'phasesDetails' object
 * and removes the selected humans from the phase requirements and students.
 *
 * @param {Object} phase - An object containing phase details.
 * @param {Object} params - An object containing parameters for setting the phase additional humans (crew).
 * @param {string} params.phaseName - The name of the phase in the 'phasesDetails' object.
 * @param {Array} params.selectedHumans - An array of additional humans (crew) selected for the phase.
 * @returns {Object} - The updated 'phasesDetails' object with the selected additional humans (crew) and removed selected humans.
 */
export function setPhaseAdditionalHumans(phase, params) {
  const { selectedHumans } = params;
  phase.additionalCrew = selectedHumans;
  map(selectedHumans, ah => {
    find(phase.requirements, req => req?.selectedResource?.value === ah?.value && unset(req, 'selectedResource'));
    remove(phase.students, stu => stu?.value?.studentId === ah?.value);
  });
  return phase;
}

/**
 * Sets the selected additional humans (crew) for all phases in the 'phasesDetails' object
 * and removes the selected humans from the phase requirements and students for each phase.
 *
 * @param {Object} phasesDetails - An object containing phase details indexed by phase names.
 * @param {Object} params - An object containing parameters for setting the additional humans (crew).
 * @returns {Object} - The updated 'phasesDetails' object with the selected additional humans (crew) and removed selected humans for all phases.
 */
export function setAndPropagateAdditionalHumans(phasesDetails, params) {
  const { propagateSource, autofills, isEdit } = params;
  forOwn(phasesDetails, (phase, key) => {
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && setPhaseAdditionalHumans(phase, params)
      : key === EVENT_PHASES.EVENT
        ? setPhaseAdditionalHumans(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && setPhaseAdditionalHumans(phase, params);
  });
  return phasesDetails;
}

/**
 * Sets the selected additional resources for all phases in the 'phasesDetails' object
 * and removes the selected resources from the phase requirements for each phase.
 *
 * @param {Object} phase - An object containing phase details.
 * @param {Object} params - An object containing parameters for setting the additional resources.
 * @param {Array} params.selectedResources - An array of additional resources selected for all phases.
 * @returns {Object} - The updated 'phasesDetails' object with the selected additional resources and removed selected resources for all phases.
 */
export function setPhaseAdditionalResources(phase, params) {
  const { selectedResources } = params;
  phase.additionalResources = selectedResources;
  map(selectedResources, ah => {
    find(phase.requirements, req => req?.selectedResource?.value === ah?.value && unset(req, 'selectedResource'));
  });
  return phase;
}

/**
 * Sets the selected additional resources for all phases in the 'phasesDetails' object
 * and removes the selected resources from the phase requirements for each phase.
 *
 * @param {Object} phasesDetails - An object containing phase details indexsed by phase names.
 * @param {Object} params - An object containing parameters for setting the additional resources.
 * @returns {Object} - The updated 'phasesDetails' object with the selected additional resources and removed selected resources for all phases.
 */
export function setAndPropagateAdditionalResources(phasesDetails, params) {
  const { propagateSource, autofills, isEdit } = params;
  forOwn(phasesDetails, (phase, key) => {
    propagateSource === PROPAGATE_SOURCE.QUICK_SETUP
      ? autofills && autofills[key] && setPhaseAdditionalResources(phase, params)
      : key === EVENT_PHASES.EVENT
        ? setPhaseAdditionalResources(phase, params)
        : (isEdit || (!isEdit && autofills && autofills[key])) && setPhaseAdditionalResources(phase, params);
  });
  return phasesDetails;
}

/**
 * Syncs flyproGroups with local storage by merging saved group data.
 */
export const syncFlyproGroupsWithLocalStorage = (flyproGroups, userId) => {
  const localStoreFlyproGroups = store.get(`flyproGroups/${userId}`);
  if (localStoreFlyproGroups) {
    map(flyproGroups, g => {
      const foundG = find(localStoreFlyproGroups, { id: g.id });
      if (foundG) assign(g, foundG);
    });
  } else {
    store.set(`flyproGroups/${userId}`, []);
  }
  return flyproGroups;
};
